import { Input } from "components/design/input";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Search } from "components/DesignSystem/Search/Search";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import {
  useGetGroupTaskListQuery,
  useGetTaskAccessListFCAQuery,
} from "store/apis/teamSetting";
import {
  ACCESS_TYPE_ALL_TASKS,
  ACCESS_TYPE_SELECTED_TASKS,
} from "constants/foreignEntityAccountant";
import { debounce } from "utils/debouncing";
import { TaskName } from "./TaskAccessModal";

type AccessFormValues = {
  accessType: string;
  taskIdList: string[];
};

export const ForeignEntityAccountantAccess = ({
  groupId,
  providerId,
}: {
  groupId: string;
  providerId?: string;
}) => {
  const { values, setFieldValue } = useFormikContext<AccessFormValues>();
  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  const { data: providerTaskList, isLoading: providerTaskListLoading } =
    useGetTaskAccessListFCAQuery(
      { searchTerm, groupId, providerId: providerId! },
      { skip: !groupId || !providerId, refetchOnMountOrArgChange: true }
    );
  const { data: allTaskList = [], isLoading: allTaskListLoading } =
    useGetGroupTaskListQuery(
      { searchTerm, groupId },
      {
        skip: !groupId || Boolean(providerId),
        refetchOnMountOrArgChange: true,
      }
    );

  const isLoading = providerTaskListLoading || allTaskListLoading;
  const taskList = providerId ? providerTaskList?.task_list ?? [] : allTaskList;
  const noTaskFound = taskList.length === 0 && !isLoading;
  const selectedTasks = new Set(values.taskIdList);

  const tasksUserHasAccess = taskList
    .filter((task) => task.has_task_access)
    .map((task) => task.uuid);

  useEffect(() => {
    if (providerId) {
      setFieldValue("taskIdList", tasksUserHasAccess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tasksUserHasAccess), providerId, setFieldValue]);

  const handleSearch = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value || null);
  });

  const handleTaskSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    if (name === "allTask") {
      const taskIdList = checked ? taskList.map(({ uuid }) => uuid) : [];
      setFieldValue("taskIdList", taskIdList);
      return;
    }

    const nextSelected = new Set(selectedTasks);
    if (checked) {
      nextSelected.add(name);
    } else {
      nextSelected.delete(name);
    }
    setFieldValue("taskIdList", Array.from(nextSelected));
  };

  return (
    <div
      role="group"
      aria-labelledby="form-selector-radio-group"
      className="t-ml-1 t-flex t-flex-col t-gap-2"
    >
      <Input
        label={
          <div className="t-cursor-pointer t-text-subtitle-sm t-font-medium">
            All tasks (includes future tasks as well)
          </div>
        }
        type="radio"
        value={ACCESS_TYPE_ALL_TASKS}
        name="accessType"
        /* @tw */
        customInputClass="!t-h-4 !t-w-4 t-cursor-pointer"
      />
      <Input
        label={
          <div className="t-cursor-pointer t-text-subtitle-sm t-font-medium">
            Select filings
          </div>
        }
        value={ACCESS_TYPE_SELECTED_TASKS}
        type="radio"
        name="accessType"
        /* @tw */
        customInputClass="!t-h-4 !t-w-4 t-cursor-pointer"
      />
      {values.accessType === ACCESS_TYPE_SELECTED_TASKS && (
        <div className="t-ml-8 t-flex t-flex-col t-gap-3">
          <Search
            onChange={handleSearch}
            customSize="regular"
            placeholder="Search for Filing"
            block
            autoFocus
          />
          {noTaskFound ? (
            <div className="t-flex t-h-56 t-items-center t-justify-center">
              No filing found
            </div>
          ) : (
            <div className="t-flex t-h-56 t-flex-col t-gap-4 t-overflow-auto t-p-2">
              <Checkbox
                onChange={handleTaskSelect}
                checked={taskList.every(({ uuid }) => selectedTasks.has(uuid))}
                label={
                  <span className="t-text-subtext">Select all filings</span>
                }
                name="allTask"
              />
              {taskList.map(({ task_title, uuid }) => (
                <Checkbox
                  key={uuid}
                  onChange={handleTaskSelect}
                  checked={selectedTasks.has(uuid)}
                  label={<TaskName task_title={task_title} />}
                  name={uuid}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
